import {CONSTANTS, UTILS} from '../../index.js';

/**
 * Gets a new resultant vector from the addition of two given vectors.
 * @param {vector3} $vector3a The vector to add on.
 * @param {vector3} $vector3b The vector to add with.
 * @returns {vector3}
 */
function add($vector3a, $vector3b) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    const vector = identity();

    vector[0] = xa + xb;
    vector[1] = ya + yb;
    vector[2] = za + zb;

    return vector;
}

/**
 * Gets a new resultant vector from the addition of two given vectors with a given decimal precision.
 * @param {vector3} $vector3a The vector to add on.
 * @param {vector3} $vector3b The vector to add with.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function addround($vector3a, $vector3b, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    const vector = identity();

    vector[0] = UTILS.round(xa + xb, $decimals);
    vector[1] = UTILS.round(ya + yb, $decimals);
    vector[2] = UTILS.round(za + zb, $decimals);

    return vector;
}

/**
 * Gets a new clone of given reference vector.
 * @param {vector3} $vector The reference vector.
 * @returns {vector3}
 */
function clone($vector) {

    return [...$vector];
}

/**
 * Creates a new vector from three given values.
 * @param {[number, number, number]} $values The vector values.
 * @returns {vector3}
 */
function create($values) {

    return [...$values];
}

/**
 * Checks the equality of two given vectors.
 * @param {vector3} $vector3a The vector to check on.
 * @param {vector3} $vector3b The vector to check with.
 * @returns {boolean}
 */
function equal($vector3a, $vector3b) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    return xa === xb
    && ya === yb
    && za === zb;
}

/**
 * Checks the equality of two given vectors with a given decimal precision.
 * @param {vector3} $vector3a The vector to check on.
 * @param {vector3} $vector3b The vector to check with.
 * @param {number} [$decimals] The decimal precision.
 * @returns {boolean}
 */
function equalround($vector3a, $vector3b, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    return UTILS.round(xa, $decimals) === UTILS.round(xb, $decimals)
    && UTILS.round(ya, $decimals) === UTILS.round(yb, $decimals)
    && UTILS.round(za, $decimals) === UTILS.round(zb, $decimals);
}

/**
 * Gets a new identity vector.
 * @returns {vector3}
 */
function identity() {

    return [0, 0, 0];
}

/**
 * Gets a new resultant vector from the multiplication of two given vectors.
 * @param {vector3} $vector3a The vector to multiply on.
 * @param {vector3} $vector3b The vector to multiply with.
 * @returns {vector3}
 */
function multiply($vector3a, $vector3b) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    const vector = identity();

    vector[0] = xa * xb;
    vector[1] = ya * yb;
    vector[2] = za * zb;

    return vector;
}

/**
 * Gets a new resultant vector from the multiplication of two given vectors with a given decimal precision.
 * @param {vector3} $vector3a The vector to multiply on.
 * @param {vector3} $vector3b The vector to multiply with.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function multiplyround($vector3a, $vector3b, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    const vector = identity();

    vector[0] = UTILS.round(xa * xb, $decimals);
    vector[1] = UTILS.round(ya * yb, $decimals);
    vector[2] = UTILS.round(za * zb, $decimals);

    return vector;
}

/**
 * Gets a new resultant vector from the multiplication of a vector by a scalar factor.
 * @param {vector3} $vector3 The vector to multiply on.
 * @param {number} $factor The scalar factor to multiply with.
 * @returns {vector3}
 */
function multiplyscalar($vector3, $factor) {

    const [xa, ya, za] = $vector3;

    const vector = identity();

    vector[0] = xa * $factor;
    vector[1] = ya * $factor;
    vector[2] = za * $factor;

    return vector;
}

/**
 * Gets a new resultant vector from the multiplication of a vector by a scalar factor with a given decimal precision.
 * @param {vector3} $vector3 The vector to multiply on.
 * @param {number} $factor The scalar factor to multiply with.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function multiplyscalarround($vector3, $factor, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [xa, ya, za] = $vector3;

    const vector = identity();

    vector[0] = UTILS.round(xa * $factor, $decimals);
    vector[1] = UTILS.round(ya * $factor, $decimals);
    vector[2] = UTILS.round(za * $factor, $decimals);

    return vector;
}

/**
 * Gets a new negative vector from given reference vector.
 * @param {vector3} $vector3 The reference vector.
 * @returns {vector3}
 */
function negate($vector3) {

    const vector = identity();

    vector[0] = - $vector3[0];
    vector[1] = - $vector3[1];
    vector[2] = - $vector3[2];

    return vector;
}

/**
 * Gets a new negative vector from given reference vector with a given decimal precision.
 * @param {vector3} $vector3 The reference vector.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function negateround($vector3, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const vector = identity();

    vector[0] = UTILS.round(- $vector3[0], $decimals);
    vector[1] = UTILS.round(- $vector3[1], $decimals);
    vector[2] = UTILS.round(- $vector3[2], $decimals);

    return vector;
}

/**
 * Gets a new normalize vector from given reference vector.
 * @param {vector3} $vector3 The reference vector.
 * @returns {vector3}
 */
function normalize($vector3) {

    const [x, y, z] = $vector3;

    const vector = identity();

    let length = x * x + y * y + z * z;

    if (length > 0) {

        length = 1 / Math.sqrt(length);
    }

    vector[0] = x * length;
    vector[1] = y * length;
    vector[2] = z * length;

    return vector;
}

/**
 * Gets a new normalize vector from given reference vector with a given decimal precision.
 * @param {vector3} $vector3 The reference vector.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function normalizeround($vector3, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [x, y, z] = $vector3;

    const vector = identity();

    let length = x * x + y * y + z * z;

    if (UTILS.round(length, $decimals) > 0) {

        length = 1 / Math.sqrt(length);
    }

    vector[0] = UTILS.round(x * length, $decimals);
    vector[1] = UTILS.round(y * length, $decimals);
    vector[2] = UTILS.round(z * length, $decimals);

    return vector;
}

/**
 * Gets a new rotated vector from given reference vector.
 * @param {vector3} $vector3 The reference vector.
 * @param {quaternion} $quaternion The rotation to apply.
 * @returns {vector3}
 */
function rotate($vector3, $quaternion) {

    const [xa, ya, za] = $vector3;
    const [xb, yb, zb, wb] = $quaternion;

    const vector = identity();

    let uvx = yb * za - zb * ya;
    let uvy = zb * xa - xb * za;
    let uvz = xb * ya - yb * xa;

    let uuvx = yb * uvz - zb * uvy;
    let uuvy = zb * uvx - xb * uvz;
    let uuvz = xb * uvy - yb * uvx;

    const w = wb * 2;

    uvx *= w;
    uvy *= w;
    uvz *= w;

    uuvx *= 2;
    uuvy *= 2;
    uuvz *= 2;

    vector[0] = xa + uvx + uuvx;
    vector[1] = ya + uvy + uuvy;
    vector[2] = za + uvz + uuvz;

    return vector;
}

/**
 * Gets a new rotated vector from given reference vector with a given decimal precision.
 * @param {vector3} $vector3 The reference vector.
 * @param {quaternion} $quaternion The rotation to apply.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function rotateround($vector3, $quaternion, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [xa, ya, za] = $vector3;
    const [xb, yb, zb, wb] = $quaternion;

    const vector = identity();

    let uvx = yb * za - zb * ya;
    let uvy = zb * xa - xb * za;
    let uvz = xb * ya - yb * xa;

    let uuvx = yb * uvz - zb * uvy;
    let uuvy = zb * uvx - xb * uvz;
    let uuvz = xb * uvy - yb * uvx;

    const w = wb * 2;

    uvx *= w;
    uvy *= w;
    uvz *= w;

    uuvx *= 2;
    uuvy *= 2;
    uuvz *= 2;

    vector[0] = UTILS.round(xa + uvx + uuvx, $decimals);
    vector[1] = UTILS.round(ya + uvy + uuvy, $decimals);
    vector[2] = UTILS.round(za + uvz + uuvz, $decimals);

    return vector;
}

/**
 * Gets a new resultant vector from the subtraction of two given vectors.
 * @param {vector3} $vector3a The vector to subtract on.
 * @param {vector3} $vector3b The vector to subtract with.
 * @returns {vector3}
 */
function subtract($vector3a, $vector3b) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    const vector = identity();

    vector[0] = xa - xb;
    vector[1] = ya - yb;
    vector[2] = za - zb;

    return vector;
}

/**
 * Gets a new resultant vector from the subtraction of two given vectors with a given decimal precision.
 * @param {vector3} $vector3a The vector to subtract on.
 * @param {vector3} $vector3b The vector to subtract with.
 * @param {number} [$decimals] The decimal precision.
 * @returns {vector3}
 */
function subtractround($vector3a, $vector3b, $decimals = CONSTANTS.PRECISION_DECIMALS_8) {

    const [xa, ya, za] = $vector3a;
    const [xb, yb, zb] = $vector3b;

    const vector = identity();

    vector[0] = UTILS.round(xa - xb, $decimals);
    vector[1] = UTILS.round(ya - yb, $decimals);
    vector[2] = UTILS.round(za - zb, $decimals);

    return vector;
}

/**
 * Gets a new resultant vector from the unprojection of given cliped/normalised vector and given unprojection matrix.
 * @param {vector3} $vector3 The cliped/normalised vector to unproject.
 * @param {matrix4} $matrix The unprojection matrix.
 * @returns {vector3}
 */
function unproject($vector3, $matrix) {

    const [x, y, z] = $vector3;

    const vector = identity();

    const [

        aa, ab, ac, ad,
        ba, bb, bc, bd,
        ca, cb, cc, cd,
        da, db, dc, dd

    ] = $matrix;

    const d = x * ad + y * bd + z * cd + dd;

    if (d === 0) {

        vector[0] = 0;
        vector[1] = 0;
        vector[2] = 0;

        return vector;
    }

    const dinverse = 1 / d;

    vector[0] = (x * aa + y * ba + z * ca + da) * dinverse;
    vector[1] = (x * ab + y * bb + z * cb + db) * dinverse;
    vector[2] = (x * ac + y * bc + z * cc + dc) * dinverse;

    return vector;
}

export {

    add,
    addround,
    clone,
    create,
    equal,
    equalround,
    identity,
    multiply,
    multiplyround,
    multiplyscalar,
    multiplyscalarround,
    negate,
    negateround,
    normalize,
    normalizeround,
    rotate,
    rotateround,
    subtract,
    subtractround,
    unproject
};
